// Call times are read and built on the Martinique wall clock, whatever the
// device's zone: the api checks the call window there (OUTSIDE_CALL_WINDOW).
// Martinique keeps UTC-4 all year (no daylight saving), so a fixed offset is exact.
export const CALL_TIME_ZONE = "America/Martinique";
const MARTINIQUE_OFFSET_MS = -4 * 3600000;
const DAY_MS = 24 * 3600000;
const MOMENT_STEP_MINUTES = 5;
const MORNING = [9, 0];
const AFTERNOON = [15, 0];
const AFTERNOON_OFFERED_BEFORE_HOUR = 14;

export const DEFAULT_CALL_RULES = {
  noAnswerThreshold: 3,
  callWindow: { start: "09:00", end: "17:00" },
};

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

// The callRules of /calls/day, with the defaults for whatever is missing or unreadable.
export const readCallRules = (rules) => {
  const threshold = rules?.noAnswerThreshold;
  const window = rules?.callWindow;
  const windowValid =
    !!window &&
    HH_MM.test(window.start) &&
    HH_MM.test(window.end) &&
    window.start < window.end;
  return {
    noAnswerThreshold:
      Number.isInteger(threshold) && threshold >= 1
        ? threshold
        : DEFAULT_CALL_RULES.noAnswerThreshold,
    callWindow: windowValid
      ? { start: window.start, end: window.end }
      : DEFAULT_CALL_RULES.callWindow,
  };
};

// A Date whose UTC fields read the Martinique wall clock.
const wall = (date) => new Date(date.getTime() + MARTINIQUE_OFFSET_MS);
const fromWall = (year, month, day, hours = 0, minutes = 0) =>
  new Date(Date.UTC(year, month, day, hours, minutes) - MARTINIQUE_OFFSET_MS);

const pad = (n) => String(n).padStart(2, "0");

export const clockParts = (date) => {
  const w = wall(date);
  return { hours: w.getUTCHours(), minutes: pad(w.getUTCMinutes()) };
};

export const clock = (date) => {
  const { hours, minutes } = clockParts(date);
  return `${hours}:${minutes}`;
};

export const atClock = (day, [hours, minutes]) => {
  const w = wall(day);
  return fromWall(
    w.getUTCFullYear(),
    w.getUTCMonth(),
    w.getUTCDate(),
    hours,
    minutes
  );
};

const startOfCallDay = (date) => atClock(date, [0, 0]);
const nextCallDay = (date) => new Date(startOfCallDay(date).getTime() + DAY_MS);
const sameCallDay = (a, b) =>
  startOfCallDay(a).getTime() === startOfCallDay(b).getTime();

// Next calendar day, skipping Sunday only.
export const nextWorkingDay = (now) => {
  const day = nextCallDay(now);
  return wall(day).getUTCDay() === 0 ? nextCallDay(day) : day;
};

const minutesOfClock = ([hours, minutes]) => hours * 60 + minutes;
const parseHhMm = (value) => value.split(":").map(Number);

// Bounds included, like the api.
export const clockInWindow = (clockValue, callWindow) => {
  const minutes = minutesOfClock(clockValue);
  return (
    minutes >= minutesOfClock(parseHhMm(callWindow.start)) &&
    minutes <= minutesOfClock(parseHhMm(callWindow.end))
  );
};

export const inCallWindow = (date, callWindow) => {
  const w = wall(date);
  return clockInWindow([w.getUTCHours(), w.getUTCMinutes()], callWindow);
};

// "09:00" -> "9:00", like the moments.
export const windowClock = (value) => {
  const [hours, minutes] = parseHhMm(value);
  return `${hours}:${pad(minutes)}`;
};

// Only moments inside the call window: today's ones outside it are dropped,
// tomorrow's morning moves to the window start when 9:00 is outside it.
export const callMoments = (now, callWindow = DEFAULT_CALL_RULES.callWindow) => {
  const step = MOMENT_STEP_MINUTES * 60000;
  const inAnHour = Math.floor(now.getTime() / 60000) * 60000 + 3600000;
  const hour = new Date(Math.ceil(inAnHour / step) * step);

  const moments = [];
  if (sameCallDay(hour, now) && inCallWindow(hour, callWindow)) {
    moments.push({ id: "hour", at: hour });
  }
  const afternoon = atClock(now, AFTERNOON);
  if (
    wall(now).getUTCHours() < AFTERNOON_OFFERED_BEFORE_HOUR &&
    inCallWindow(afternoon, callWindow)
  ) {
    moments.push({ id: "afternoon", at: afternoon });
  }
  const tomorrow = nextWorkingDay(now);
  const morning = atClock(tomorrow, MORNING);
  moments.push({
    id: "tomorrow",
    at: inCallWindow(morning, callWindow)
      ? morning
      : atClock(tomorrow, parseHhMm(callWindow.start)),
    nextDay: sameCallDay(tomorrow, nextCallDay(now)),
  });
  return moments;
};

export const defaultRetry = (moments) =>
  moments.some((moment) => moment.id === "afternoon") ? "afternoon" : "tomorrow";

export const parseClock = (text) => {
  const match = /^(\d{1,2})\s*[:hH.]?\s*(\d{2})?$/.exec(String(text ?? "").trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  return hours > 23 || minutes > 59 ? null : [hours, minutes];
};

// day ('YYYY-MM-DD') and the time typed by hand are both read in Martinique.
export const resolveWhen = (
  { choice, day, time },
  moments,
  now,
  callWindow = DEFAULT_CALL_RULES.callWindow
) => {
  if (choice !== "other") {
    return moments.find((moment) => moment.id === choice)?.at ?? null;
  }
  const clockValue = parseClock(time);
  if (!day || !clockValue || !clockInWindow(clockValue, callWindow)) return null;
  const [year, month, date] = day.split("-").map(Number);
  const at = fromWall(year, month - 1, date, ...clockValue);
  return at > now ? at : null;
};

export const longDay = (date, locale = "fr") =>
  new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: CALL_TIME_ZONE,
  }).format(date);

export const weekdayName = (date, locale = "fr") =>
  new Intl.DateTimeFormat(locale, {
    weekday: "long",
    timeZone: CALL_TIME_ZONE,
  }).format(date);

export const whenKey = (at, now) => {
  if (sameCallDay(at, now)) return "today";
  return sameCallDay(at, nextCallDay(now)) ? "tomorrowAt" : "onDay";
};
